import { useCallback, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "./Toast";
import { localizedName } from "../lib/localizedName";
import { formatDate } from "../lib/format";
import Ic from "./icons.jsx";
import type { ApiError } from "../api/client";
import {
  externalAnalysisApi,
  type ExternalAnalysis,
  type ExternalPurpose,
  type ExternalValue,
} from "../api/externalAnalyses";

/**
 * Nalazi spoljne laboratorije uz jedan predmet: uz LOT, ili uz isporuku.
 *
 * Stoji na istom ekranu sa nalazima naše laboratorije zato što onaj ko čita nalaz treba da vidi i ono što
 * je merila spoljna laboratorija. Deo analiza se po pravilu ne radi u kući — mikrobiologija, gluten,
 * ostatak heksana — pa ekran koji prikazuje samo svoje nalaze o lotu kaže manje nego što se o njemu zna, a
 * izgleda kao da je to sve.
 *
 * Panel je sporedan uz ono zbog čega se ekran otvara, pa greška u čitanju ne obara stranicu: ako nalaza
 * nema ili se spisak ne može pročitati, panela nema.
 */
export function ExternalAnalysesPanel({
  subject,
}: {
  subject: { kind: "lot"; id: string } | { kind: "delivery"; id: string };
}) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const { hasPermission } = useAuth();
  const lang = i18n.resolvedLanguage ?? "sr";
  // Sam izveštaj se preuzima pod pravom za spoljne analize; spisak otvaraju i prava ekrana na kojem
  // panel stoji, pa dugmeta za preuzimanje nema onome kome bi vratilo odbijenicu.
  const smeDaPreuzme = hasPermission("external_analyses:view");

  const [rows, setRows] = useState<ExternalAnalysis[]>([]);

  const load = useCallback(async () => {
    try {
      setRows(
        subject.kind === "lot"
          ? await externalAnalysisApi.forGoodsLot(subject.id)
          : await externalAnalysisApi.forDelivery(subject.id),
      );
    } catch {
      setRows([]);
    }
  }, [subject.kind, subject.id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function download(a: ExternalAnalysis) {
    try {
      const { url } = await externalAnalysisApi.downloadUrl(a.id);
      window.open(url, "_blank", "noopener");
    } catch (e) {
      toast.error((e as ApiError).message);
    }
  }

  if (rows.length === 0) return null;

  const bezVrednosti = rows.filter((a) => a.values.length === 0).length;

  return (
    <div className="card">
      <header className="card-h">
        <div>
          <h3>{t("external.panel.title", "Nalazi spoljne laboratorije")}</h3>
          <div className="sub">
            {t("external.panel.lead", "Izmereno van Bankoma, uz isti predmet kao i nalazi laboratorije.")}
          </div>
        </div>
        <span className="tag neutral">
          {t("external.panel.count", "{{n}} nalaza", { n: rows.length })}
        </span>
      </header>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {rows.map((a) => (
          <div key={a.id} style={red}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <strong style={{ fontSize: 13 }}>
                {a.laboratoryName ?? t("external.panel.noLab", "laboratorija nije upisana")}
              </strong>
              {a.reportNumber && <span style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{a.reportNumber}</span>}
              <span className="tag neutral">{purposeLabel(t, a.purpose)}</span>
              {a.verifiedAt && <span className="tag ok">{t("external.panel.verified", "potvrđen")}</span>}
              <span style={{ ...tiho, marginLeft: "auto" }}>
                {a.issuedOn
                  ? t("external.panel.issuedOn", "izdat {{datum}}", { datum: formatDate(a.issuedOn, lang) })
                  : t("external.panel.noIssueDate", "datum izdavanja nije upisan")}
              </span>
              {a.hasFile && smeDaPreuzme && (
                <button type="button" className="btn sm" onClick={() => void download(a)}>
                  <Ic.pdf /> {t("external.panel.download", "Izveštaj")}
                </button>
              )}
            </div>

            <div style={{ ...tiho, marginTop: 2 }}>{a.subject}</div>

            {a.values.length === 0 ? (
              <div style={{ ...tiho, marginTop: 6 }}>
                {t(
                  "external.panel.fileOnly",
                  "Uz nalaz nisu upisane vrednosti, pa se ne može ni pretražiti ni uporediti — čita se iz izveštaja.",
                )}
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                {a.values.map((v) => (
                  <span key={v.parameterId} className="tag neutral">
                    {localizedName({ name: v.key, nameI18n: v.nameI18n }, lang)}
                    <strong style={{ marginLeft: 6 }}>{valueText(t, v)}</strong>
                    {v.unit ? <span className="muted" style={{ marginLeft: 4 }}>{v.unit}</span> : null}
                  </span>
                ))}
              </div>
            )}

            {a.note && <div style={{ ...tiho, marginTop: 4 }}>{a.note}</div>}
          </div>
        ))}

        {bezVrednosti > 0 && (
          <span style={tiho}>
            {t("external.panel.withoutValues", "Od {{n}} nalaza, {{bez}} nosi samo izveštaj bez upisanih vrednosti.", {
              n: rows.length,
              bez: bezVrednosti,
            })}
          </span>
        )}
      </div>
    </div>
  );
}

function purposeLabel(t: (k: string, d: string) => string, p: ExternalPurpose): string {
  if (p === "FOR_CUSTOMER") return t("external.purpose.FOR_CUSTOMER", "za kupca");
  if (p === "DISPUTE") return t("external.purpose.DISPUTE", "superanaliza");
  return t("external.purpose.INTERNAL", "interno");
}

/** Jedna vrednost se piše onako kako je izmerena: broj, da-ne, ili tekst. */
function valueText(t: (k: string, d: string) => string, v: ExternalValue): string {
  if (v.valueNum != null) return String(v.valueNum);
  if (v.valueBool != null) {
    return v.valueBool ? t("external.panel.yes", "da") : t("external.panel.no", "ne");
  }
  return v.valueText ?? "—";
}

const tiho: CSSProperties = { color: "var(--ink-3)", fontSize: 12.5 };

const red: CSSProperties = {
  border: "1px solid var(--border)",
  borderRadius: "var(--r-sm)",
  padding: "10px 12px",
};
